/**
 * 2D map (spec §2.3, §4.7): MapLibre GL with a custom raster protocol that
 * copies the canvas into Web Mercator tiles. No basemap — the canvas is
 * the map. Handles browsing, drawing, playback and report-region selection.
 *
 * Alive touches: a faint graticule for orientation, a breathing draw area,
 * and a pop where each fresh pixel lands. All motion respects
 * prefers-reduced-motion.
 */
import * as maplibregl from 'maplibre-gl';
import type { LngLatBoundsLike, Map as MLMap, MapMouseEvent, MapTouchEvent, RequestParameters } from 'maplibre-gl';
import type { Feature, FeatureCollection, LineString, Point, Polygon } from 'geojson';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef } from 'react';
import { TILE_ZOOM, activityCellFromToken, activityCellKey, idxToLocalIJ, pixelCenterLatLng, pixelCornersLatLng, pixelFromLatLng, tileFromKey, tileFromToken, tileKey, tileToken, tilesCoveringBounds, type TileRef } from '@drawearth/geo';
import { PALETTE, type LivePainter, type TileRegion } from '@drawearth/shared';
import { avatarDataUrl } from './Widgets.tsx';
import { api } from '../lib/api.ts';
import { onThemeChange, themeColours } from '../lib/theme.ts';
import type { Painter } from '../lib/painter.ts';
import { appStore, useApp, type Mode, type Position } from '../lib/store.ts';
import type { TileStore } from '../lib/tiles.ts';
import { modeForZoom, tileToBounds, FULL_RES_MIN_ZOOM } from '../render/mercator.ts';
import { packRGBA } from '../render/raster.ts';
import type { RendererClient } from '../render/client.ts';

export interface MapViewProps {
  store: TileStore;
  renderer: RendererClient;
  painter: Painter | null;
  center: { lat: number; lng: number };
  zoom: number;
  /** Increment to fly to the current device position. */
  locateSignal?: number;
  onViewChange?: (v: { lat: number; lng: number; zoom: number }) => void;
  onRegionSelected?: (tile: string, region: TileRegion) => void;
  onHoverPixel?: (info: { tile: string; idx: number; colour: number } | null) => void;
}

const PROTOCOL = 'drawearth';
const SOURCE = 'canvas';
/** Below this zoom the world is drawn as a sphere; above FLAT_MIN_ZOOM it is flat. */
const GLOBE_MAX_ZOOM = 5;
const FLAT_MIN_ZOOM = 8;

// MapLibre does not re-export its expression types, so these builders take the
// paint property's own type from the call site.
/** Blend a value across the sphere-to-flat transition. */
function acrossProjection<T>(sphere: unknown, flat: unknown): T {
  return ['interpolate', ['linear'], ['zoom'], GLOBE_MAX_ZOOM, sphere, FLAT_MIN_ZOOM, flat] as unknown as T;
}
/**
 * The planet keeps the canvas colour at every scale — fading it would wash the
 * whole screen grey mid-zoom. Against space it reads by its atmosphere and a
 * graticule that brightens while the world is a sphere.
 */
function graticuleOpacity<T>(c: ReturnType<typeof themeColours>): T {
  const [major, minor] = c.sphereGridAlpha;
  return acrossProjection<T>(['case', ['get', 'major'], major, minor], ['case', ['get', 'major'], 0.22, 0.09]);
}
/** Atmosphere: a rim of light around the globe, gone by the time the map is flat. */
function skySpec(c: ReturnType<typeof themeColours>): Record<string, unknown> {
  return {
    'sky-color': c.ground,
    'horizon-color': c.halo,
    'fog-color': c.ground,
    'sky-horizon-blend': 0.6,
    'horizon-fog-blend': 0.6,
    'fog-ground-blend': 0.2,
    'atmosphere-blend': acrossProjection(0.9, 0),
  };
}
const reduceMotion = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

// The protocol handler is global to MapLibre; route it to the active view.
let activeHandler: ((z: number, x: number, y: number) => Promise<ArrayBuffer>) | null = null;
let protocolRegistered = false;
function registerProtocol(): void {
  if (protocolRegistered) return;
  protocolRegistered = true;
  maplibregl.addProtocol(PROTOCOL, async (params: RequestParameters) => {
    const dbg = (window as unknown as { __de?: { calls: unknown[] } }).__de;
    dbg?.calls.push({ stage: 'protocol', url: params.url, type: params.type });
    const m = /^drawearth:\/\/canvas\/(\d+)\/(\d+)\/(\d+)/.exec(params.url);
    if (!m || !activeHandler) return { data: new ArrayBuffer(0) };
    try {
      const data = await activeHandler(Number(m[1]), Number(m[2]), Number(m[3]));
      return { data };
    } catch (err) {
      dbg?.calls.push({ stage: 'error', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  });
}

function paletteRgb(i: number): string {
  const c = PALETTE[i & 0xff];
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

function circlePolygon(lat: number, lng: number, radiusM: number, n = 72): Feature<Polygon> {
  const coords: [number, number][] = [];
  const dLat = radiusM / 111_320;
  const dLng = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180));
  for (let k = 0; k <= n; k++) {
    const a = (k / n) * 2 * Math.PI;
    coords.push([lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } };
}

/**
 * The outline of one canvas pixel. On the Mercator grid a pixel is already an
 * axis-aligned square, so its own corners are the outline.
 */
function pixelPolygon(ref: TileRef, idx: number): Feature<Polygon> {
  const c = pixelCornersLatLng(ref, idx).map((p) => [p.lng, p.lat] as [number, number]);
  c.push(c[0]);
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [c] } };
}

/** A 10° graticule: the only orientation aid on an otherwise empty planet. */
function graticule(): FeatureCollection<LineString> {
  const features: Feature<LineString>[] = [];
  for (let lng = -180; lng <= 180; lng += 10) {
    features.push({ type: 'Feature', properties: { major: lng % 90 === 0 }, geometry: { type: 'LineString', coordinates: [[lng, -85], [lng, 85]] } });
  }
  for (let lat = -80; lat <= 80; lat += 10) {
    const coords: [number, number][] = [];
    for (let lng = -180; lng <= 180; lng += 5) coords.push([lng, lat]);
    features.push({ type: 'Feature', properties: { major: lat === 0 }, geometry: { type: 'LineString', coordinates: coords } });
  }
  return { type: 'FeatureCollection', features };
}

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

export function MapView(props: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MLMap | null>(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const mode = useApp((s) => s.mode);
  const position = useApp((s) => s.position);
  const colour = useApp((s) => s.colour);
  const params = useApp((s) => s.params);
  const stateRef = useRef({ mode, position, colour, params });
  stateRef.current = { mode, position, colour, params };
  const summaryLoaded = useRef<{ at: number; promise: Promise<void> | null }>({ at: 0, promise: null });
  const versionRef = useRef(0);
  const popsRef = useRef<Array<{ lng: number; lat: number; born: number; colour: string }>>([]);
  const markersRef = useRef<Map<number, { marker: maplibregl.Marker; el: HTMLElement; ts: number; key: number }>>(new Map());

  // --- map lifecycle ---------------------------------------------------
  useEffect(() => {
    if (!containerRef.current) return;
    registerProtocol();
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {},
        // Sphere when you pull back, flat once you are close enough to see
        // streets — the canvas is one surface at every scale (spec §2.3).
        projection: { type: ['interpolate', ['linear'], ['zoom'], GLOBE_MAX_ZOOM, 'vertical-perspective', FLAT_MIN_ZOOM, 'mercator'] },
        sky: skySpec(themeColours()) as maplibregl.StyleSpecification['sky'],
        layers: [{ id: 'bg', type: 'background', paint: { 'background-color': themeColours().ground } }],
      },
      center: [props.center.lng, props.center.lat],
      zoom: props.zoom,
      minZoom: 0,
      maxZoom: 22,
      attributionControl: false,
      renderWorldCopies: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      fadeDuration: 0,
    });
    mapRef.current = map;
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    const ensureSummaries = async (): Promise<void> => {
      const now = Date.now();
      if (summaryLoaded.current.promise) return summaryLoaded.current.promise;
      if (now - summaryLoaded.current.at < 60_000) return;
      summaryLoaded.current.promise = (async () => {
        try {
          const [cells, tiles] = await Promise.all([api.globeCells(), api.globeTiles()]);
          const byCell: Array<[number, number]> = cells.map((c) => [activityCellKey(activityCellFromToken(c.cell)), packRGBA(c.r, c.g, c.b, 255)]);
          const byTile: Array<[number, number]> = tiles.map((t) => [tileKey(tileFromToken(t.cell)), packRGBA(t.r, t.g, t.b, 255)]);
          propsRef.current.renderer.setSummary('cell', byCell);
          propsRef.current.renderer.setSummary('tile', byTile);
          summaryLoaded.current.at = Date.now();
        } finally {
          summaryLoaded.current.promise = null;
        }
      })();
      return summaryLoaded.current.promise;
    };

    const debug = import.meta.env.DEV ? ((window as unknown as { __de?: Record<string, unknown> }).__de ??= { calls: [] as unknown[] }) : null;
    if (debug) Object.assign(debug, { map, store: propsRef.current.store, renderer: propsRef.current.renderer });
    activeHandler = async (z, x, y) => {
      const { store, renderer } = propsRef.current;
      const m = modeForZoom(z);
      let refs: TileRef[] = [];
      if (m === 'full') {
        const b0 = tileToBounds(z, x, y);
        refs = tilesCoveringBounds({ south: b0.south, west: b0.west, north: b0.north, east: b0.east });
        await store.ensureAll(refs);
      } else {
        await ensureSummaries();
      }
      const png = await renderer.render(z, x, y, 256, m);
      if (debug) (debug.calls as unknown[]).push({ z, x, y, m, refs: refs.map((r) => ({ key: tileKey(r), loaded: store.getPixels(r) === undefined ? 'unknown' : store.getPixels(r) === null ? 'empty' : 'data' })), png: png.byteLength });
      return png;
    };

    map.on('load', () => {
      map.addSource('graticule', { type: 'geojson', data: graticule() });
      map.addLayer({ id: 'graticule', type: 'line', source: 'graticule', paint: { 'line-color': themeColours().graticule, 'line-opacity': graticuleOpacity(themeColours()), 'line-width': 1 } });
      // The canvas lives at TILE_ZOOM, so that is the deepest tile worth asking
      // for; past it MapLibre magnifies with nearest neighbour, which lands
      // canvas pixels on exact square blocks for free.
      map.addSource(SOURCE, { type: 'raster', tiles: [`${PROTOCOL}://canvas/{z}/{x}/{y}?v=0`], tileSize: 256, minzoom: 0, maxzoom: TILE_ZOOM });
      map.addLayer({ id: 'canvas', type: 'raster', source: SOURCE, paint: { 'raster-resampling': 'nearest', 'raster-fade-duration': 0 } });
      map.addSource('area', { type: 'geojson', data: EMPTY });
      map.addLayer({ id: 'area-fill', type: 'fill', source: 'area', paint: { 'fill-color': '#ffc233', 'fill-opacity': 0.07 } });
      map.addLayer({ id: 'area-line', type: 'line', source: 'area', paint: { 'line-color': '#ffc233', 'line-width': 2, 'line-opacity': 0.8, 'line-dasharray': [2, 2] } });
      map.addSource('me', { type: 'geojson', data: EMPTY });
      map.addLayer({ id: 'me-halo', type: 'circle', source: 'me', paint: { 'circle-radius': 14, 'circle-color': '#58b6ff', 'circle-opacity': 0.25 } });
      map.addLayer({ id: 'me-dot', type: 'circle', source: 'me', paint: { 'circle-radius': 6, 'circle-color': '#58b6ff', 'circle-stroke-color': '#0b0d14', 'circle-stroke-width': 2 } });
      map.addSource('hover', { type: 'geojson', data: EMPTY });
      map.addLayer({ id: 'hover-line', type: 'line', source: 'hover', paint: { 'line-color': '#ffffff', 'line-width': 2 } });
      map.addSource('select', { type: 'geojson', data: EMPTY });
      map.addLayer({ id: 'select-fill', type: 'fill', source: 'select', paint: { 'fill-color': '#ff6b5b', 'fill-opacity': 0.2 } });
      map.addLayer({ id: 'select-line', type: 'line', source: 'select', paint: { 'line-color': '#ff6b5b', 'line-width': 2 } });
      map.addSource('pops', { type: 'geojson', data: EMPTY });
      map.addLayer({
        id: 'pops',
        type: 'circle',
        source: 'pops',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 't'], 0, 4, 1, 26],
          'circle-color': ['get', 'colour'],
          'circle-opacity': ['interpolate', ['linear'], ['get', 't'], 0, 0.9, 1, 0],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-opacity': ['interpolate', ['linear'], ['get', 't'], 0, 0.9, 1, 0],
        },
      });
      updateSubscriptions();
      syncOverlays();
    });
    const applyTheme = (): void => {
      const c = themeColours();
      if (!map.getLayer('bg')) return;
      map.setPaintProperty('bg', 'background-color', c.ground);
      map.setPaintProperty('graticule', 'line-color', c.graticule);
      map.setPaintProperty('graticule', 'line-opacity', graticuleOpacity(c));
      map.setSky(skySpec(c) as Parameters<MLMap['setSky']>[0]);
      map.setPaintProperty('me-halo', 'circle-color', c.halo);
      map.setPaintProperty('me-dot', 'circle-color', c.halo);
      map.setPaintProperty('me-dot', 'circle-stroke-color', c.ground);
    };
    const unsubTheme = onThemeChange(applyTheme);

    const updateSubscriptions = (): void => {
      const { store } = propsRef.current;
      const z = map.getZoom();
      if (z < FULL_RES_MIN_ZOOM) {
        store.subscribe([]);
        return;
      }
      const b = map.getBounds();
      const refs = tilesCoveringBounds({ south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast() });
      store.subscribe(refs.slice(0, stateRef.current.params.max_tile_subscriptions));
    };

    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    const refresh = (): void => {
      if (refreshTimer) return;
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        const src = map.getSource(SOURCE) as maplibregl.RasterTileSource | undefined;
        if (src) src.setTiles([`${PROTOCOL}://canvas/{z}/{x}/{y}?v=${++versionRef.current}`]);
      }, 200);
    };
    const unsubStore = propsRef.current.store.onChange(() => refresh());

    map.on('moveend', () => {
      updateSubscriptions();
      const c = map.getCenter();
      propsRef.current.onViewChange?.({ lat: c.lat, lng: c.lng, zoom: map.getZoom() });
      syncCalloutVisibility();
    });

    // --- live painters: small callouts with avatar + handle (opt-in only) ---
    const calloutsAllowed = (): boolean => map.getZoom() >= FULL_RES_MIN_ZOOM && appStore.get().mode !== 'playback';
    const syncCalloutVisibility = (): void => {
      const show = calloutsAllowed();
      for (const m of markersRef.current.values()) m.el.classList.toggle('is-hidden', !show);
    };
    const upsertCallout = (key: number, p: LivePainter): void => {
      const ref = tileFromKey(key);
      const c = pixelCenterLatLng(ref, p.idx);
      let entry = markersRef.current.get(p.userId);
      if (!entry) {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'callout callout--link';
        el.dataset.userId = String(p.userId);
        const av = document.createElement('div');
        av.className = 'callout__avatar';
        el.append(av);
        const marker = new maplibregl.Marker({ element: el, anchor: 'bottom', offset: [0, -10] }).setLngLat([c.lng, c.lat]).addTo(map);
        entry = { marker, el, ts: p.ts, key };
        markersRef.current.set(p.userId, entry);
        // Tapping a live painter drops onto the pixel they are working on.
        const self = entry;
        el.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const at = self.marker.getLngLat();
          map.flyTo({ center: at, zoom: Math.max(map.getZoom(), 19), duration: 1400, essential: true });
        });
        requestAnimationFrame(() => el.classList.add('is-in'));
      } else {
        entry.marker.setLngLat([c.lng, c.lat]);
        entry.ts = p.ts;
        entry.key = key;
        entry.el.classList.remove('is-idle');
      }
      const av = entry.el.querySelector('.callout__avatar') as HTMLElement;
      const url = avatarDataUrl(p.avatar);
      av.style.backgroundImage = url ? `url(${url})` : '';
      av.classList.toggle('callout__avatar--empty', !url);
      entry.el.title = p.handle;
      entry.el.setAttribute('aria-label', `Go to where ${p.handle} is painting`);
      entry.el.classList.toggle('is-hidden', !calloutsAllowed());
    };
    const unsubPresence = propsRef.current.store.onPresence((key, painters) => {
      for (const p of painters) upsertCallout(key, p);
    });
    // Seed from tiles already loaded, and age callouts out.
    const calloutTimer = setInterval(() => {
      const now = Date.now();
      for (const [uid, m] of markersRef.current) {
        const age = now - m.ts;
        if (age > 10 * 60_000) {
          m.marker.remove();
          markersRef.current.delete(uid);
        } else m.el.classList.toggle('is-idle', age > 90_000);
      }
    }, 5000);

    // --- overlays (draw area, position, hover) ---
    const syncOverlays = (): void => {
      // Read the store directly: this runs before React has re-rendered stateRef.
      const { mode: m, position: p, params: prm } = appStore.get();
      const area = map.getSource('area') as maplibregl.GeoJSONSource | undefined;
      const me = map.getSource('me') as maplibregl.GeoJSONSource | undefined;
      if (!area || !me) return;
      if (p && (m === 'draw' || m === 'browse')) {
        me.setData({ type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } }] });
      } else me.setData(EMPTY);
      if (p && m === 'draw') area.setData({ type: 'FeatureCollection', features: [circlePolygon(p.lat, p.lng, prm.draw_radius_m)] });
      else area.setData(EMPTY);
    };
    const unsubApp = appStore.subscribe(() => syncOverlays());

    // --- alive: breathing draw area + pixel pops ---
    let raf = 0;
    let animating = false;
    const animate = (): void => {
      const now = performance.now();
      const alive = popsRef.current.filter((p) => now - p.born < 520);
      popsRef.current = alive;
      const src = map.getSource('pops') as maplibregl.GeoJSONSource | undefined;
      if (src) {
        const fc: FeatureCollection<Point> = {
          type: 'FeatureCollection',
          features: alive.map((p) => ({ type: 'Feature', properties: { t: Math.min(1, (now - p.born) / 520), colour: p.colour }, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } })),
        };
        src.setData(fc);
      }
      const drawing = stateRef.current.mode === 'draw';
      if (drawing && map.getLayer('area-line')) {
        const t = (now % 2600) / 2600;
        map.setPaintProperty('area-line', 'line-opacity', 0.55 + 0.35 * Math.sin(t * Math.PI * 2));
        map.setPaintProperty('me-halo', 'circle-radius', 12 + 4 * Math.sin(t * Math.PI * 2));
      }
      if (drawing || alive.length) raf = requestAnimationFrame(animate);
      else animating = false;
    };
    const kick = (): void => {
      if (animating || reduceMotion) return;
      animating = true;
      raf = requestAnimationFrame(animate);
    };
    const unsubMode = appStore.subscribe(() => {
      if (appStore.get().mode === 'draw') kick();
      syncCalloutVisibility();
    });

    // --- pointer interaction ---
    let painting = false;
    let selectStart: { lng: number; lat: number } | null = null;
    const hoverSrc = () => map.getSource('hover') as maplibregl.GeoJSONSource | undefined;
    const selectSrc = () => map.getSource('select') as maplibregl.GeoJSONSource | undefined;

    const paintAt = (lngLat: maplibregl.LngLat): void => {
      const { painter } = propsRef.current;
      if (!painter) return;
      const colourIdx = stateRef.current.colour;
      const ok = painter.paintAt(lngLat.lat, lngLat.lng, colourIdx);
      if (ok && !reduceMotion) {
        const px = pixelFromLatLng(lngLat.lat, lngLat.lng);
        const c = pixelCenterLatLng(px.tile, px.idx);
        const last = popsRef.current[popsRef.current.length - 1];
        if (!last || Math.abs(last.lng - c.lng) > 1e-9 || Math.abs(last.lat - c.lat) > 1e-9) {
          popsRef.current.push({ lng: c.lng, lat: c.lat, born: performance.now(), colour: paletteRgb(colourIdx) });
          kick();
        }
      }
    };

    const onDown = (e: MapMouseEvent | MapTouchEvent): void => {
      const m = stateRef.current.mode;
      if (m === 'draw' && propsRef.current.painter && map.getZoom() >= FULL_RES_MIN_ZOOM) {
        painting = true;
        map.dragPan.disable();
        paintAt(e.lngLat);
        e.preventDefault();
      } else if (m === 'report' && map.getZoom() >= FULL_RES_MIN_ZOOM) {
        selectStart = { lng: e.lngLat.lng, lat: e.lngLat.lat };
        map.dragPan.disable();
        e.preventDefault();
      }
    };
    const onMove = (e: MapMouseEvent | MapTouchEvent): void => {
      if (painting) {
        paintAt(e.lngLat);
        return;
      }
      if (selectStart) {
        const s = selectStart;
        const poly: [number, number][] = [[s.lng, s.lat], [e.lngLat.lng, s.lat], [e.lngLat.lng, e.lngLat.lat], [s.lng, e.lngLat.lat], [s.lng, s.lat]];
        selectSrc()?.setData({ type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [poly] } }] });
        return;
      }
      if (map.getZoom() >= 17) {
        const px = pixelFromLatLng(e.lngLat.lat, e.lngLat.lng);
        hoverSrc()?.setData({ type: 'FeatureCollection', features: [pixelPolygon(px.tile, px.idx)] });
        propsRef.current.onHoverPixel?.({ tile: tileToken(px.tile), idx: px.idx, colour: propsRef.current.store.colourAt(px.tile, px.idx) });
      } else {
        hoverSrc()?.setData(EMPTY);
        propsRef.current.onHoverPixel?.(null);
      }
    };
    const onUp = (e: MapMouseEvent | MapTouchEvent): void => {
      if (painting) {
        painting = false;
        map.dragPan.enable();
        void propsRef.current.painter?.flush();
      }
      if (selectStart) {
        const s = selectStart;
        selectStart = null;
        map.dragPan.enable();
        selectSrc()?.setData(EMPTY);
        const a = pixelFromLatLng(s.lat, s.lng);
        const b = pixelFromLatLng(e.lngLat.lat, e.lngLat.lng);
        const ref = a.tile;
        const ija = idxToLocalIJ(ref, a.idx);
        const ijb = tileKey(b.tile) === tileKey(ref) ? idxToLocalIJ(ref, b.idx) : { li: ija.li, lj: ija.lj };
        const region: TileRegion = { i0: Math.min(ija.li, ijb.li), j0: Math.min(ija.lj, ijb.lj), i1: Math.max(ija.li, ijb.li), j1: Math.max(ija.lj, ijb.lj) };
        propsRef.current.onRegionSelected?.(tileToken(ref), region);
      }
    };
    map.on('mousedown', onDown);
    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
    map.on('touchstart', onDown);
    map.on('touchmove', onMove);
    map.on('touchend', onUp);

    return () => {
      unsubStore();
      unsubApp();
      unsubMode();
      unsubPresence();
      unsubTheme();
      clearInterval(calloutTimer);
      for (const m of markersRef.current.values()) m.marker.remove();
      markersRef.current.clear();
      cancelAnimationFrame(raf);
      if (refreshTimer) clearTimeout(refreshTimer);
      activeHandler = null;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Jump to a new centre when the deep link changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    if (Math.abs(c.lat - props.center.lat) > 1e-7 || Math.abs(c.lng - props.center.lng) > 1e-7 || Math.abs(map.getZoom() - props.zoom) > 0.01) {
      map.jumpTo({ center: [props.center.lng, props.center.lat], zoom: props.zoom });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.center.lat, props.center.lng, props.zoom]);

  // Fly to the device position on request.
  useEffect(() => {
    const map = mapRef.current;
    const p = stateRef.current.position;
    if (!map || !props.locateSignal || !p) return;
    map.flyTo({ center: [p.lng, p.lat], zoom: Math.max(map.getZoom(), 18), speed: 1.4, curve: 1.4, essential: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.locateSignal]);

  return <div ref={containerRef} className={`map ${mode === 'draw' ? 'map--draw' : ''} ${mode === 'report' ? 'map--report' : ''}`} data-testid="map" />;
}

export type { Mode, Position, LngLatBoundsLike };
